import moment from 'moment-timezone';
import {isFinite, get} from 'lodash';
import {memoize} from '@packages/utilities/memo';
import {concat, constant, isNil} from '@packages/utilities/fp/utils';
import {
  isDynamoItem,
  isDynamoList,
  isDynamoWrapper,
} from '@packages/utilities/elementTypeUtils';
import {
  fieldSlug as formatFieldSlug,
  collectionSlug as formatCollectionSlug,
} from '@packages/systems/dynamo/utils/SlugUtils';
import {
  getFieldBySlug,
  SortData,
} from '@packages/systems/dynamo/utils/CollectionUtils';
import {castItemValue} from '@packages/systems/dynamo/utils/DynamoItemUtils';
import {
  getDynamoItemElement,
  isPaginatedDynamoWrapperElement,
  dynQuerySortPath,
  usePrimaryLocalePath,
  dynQueryFilterPath,
  dynQueryCuratedPath,
  getDynContextFilterValuesFromFilterExpr,
  getDynListQueryModeFromElement,
  getLegacyDynContextConditionValues,
  getDynContextConditionValue,
  PAGINATION_MAX_LIMIT,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';
import {
  calculatePaginationLimitQueryArg,
  calculatePaginationSkipQueryArg,
  capDynamoWrapperLimit,
  getPaginationPageNumber,
} from '@packages/systems/dynamo/utils/PaginationUtils';
import {
  COLLECTION_LIST_QUERY_MODES,
  NON_EXISTING_ITEM_ID,
  QUERY_FILTER_FOR_STATES,
} from '@packages/systems/dynamo/constants';

import {Expression, ExpressionElement} from '@packages/systems/wfdl-ast';
import type {CollectionRecordType as CollectionType} from '@packages/systems/dynamo/records/CollectionRecord';
import type {
  ElementQuerySpecification,
  QueryPath,
  QueryPaths,
  FieldDef,
  RawData,
  RuntimeInfo,
  PaginationPageNumbers,
  ParentContextInterface,
  ExpressionEvaluationInterface,
} from '@packages/systems/dynamo/source-types';
import type {
  FieldProps,
  FieldType as Field,
} from '@packages/systems/dynamo/records/Field';
import type {ConditionsT, ConditionT} from '@packages/systems/dynamo/types';
import {RecordOf, Map, List} from 'immutable';
import {
  ExpressionPath,
  Expressions,
  matchExpression,
} from '@packages/systems/wfdl';
import {clamp} from '@packages/systems/core/utils/NumberUtils';
import {NON_CUSTOM_FIELDS_TYPES} from '../binding-sources';

// Webflow CMS doesn't allow Numbers this big
const NON_EXISTING_NUMBER = 999999999999999999999999;

type FieldDefOrNull = (arg1: RawData) => string | null;

type ValueWithEvaluationManifest<ValueT> = {
  needsComponentData: boolean;
  value: ValueT;
};

type MaybeDataDependentValueWithEvaluationManifest<ValueT> =
  ValueWithEvaluationManifest<MaybeDataDependentValue<ValueT>>;

type MaybeDataDependentValue<ValueT> = ValueT | ((data: RawData) => ValueT);

const alias =
  (fieldName: string) =>
  (nodeId: string, instanceHash?: string | null): string => {
    const aliasSuffix = instanceHash ? `__inst__${instanceHash}` : '';
    return `${fieldName}_${nodeId.replace(/-/g, '_')}${aliasSuffix}`;
  };

const itemsFieldAlias = alias('list');

type FilterAllKey = 'matchAll';
type FilterAnyKey = 'matchAny';

const FILTER_ALL: FilterAllKey = 'matchAll';
const FILTER_ANY: FilterAnyKey = 'matchAny';

const FILTER_FOR_KEY_MAP = {
  [QUERY_FILTER_FOR_STATES.ALL]: FILTER_ALL,
  [QUERY_FILTER_FOR_STATES.ANY]: FILTER_ANY,
} as const;

function specifyCollectionListBoundaryCreatorQueryPaths(
  element: ExpressionElement,
  _runtimeInfo: RuntimeInfo,
  parentContext: ParentContextInterface | null,
  exprContext: ExpressionEvaluationInterface
): QueryPaths {
  if (!parentContext) {
    return [];
  }

  const filterValues = getDynContextFilterValues(element, exprContext);
  const conditionValues = getItemDynContextConditionValues(element);
  const allDynContextValues = filterValues.concat(conditionValues);

  return getDynamicContextQueryPaths(allDynContextValues, parentContext);
}

function specifyItemRefSetListBoundaryCreatorQueryPaths(
  element: ExpressionElement,
  _runtimeInfo: RuntimeInfo,
  parentContext: ParentContextInterface | null
): QueryPaths {
  if (!parentContext) {
    return [];
  }

  const conditionValues = getItemDynContextConditionValues(element);
  return getDynamicContextQueryPaths(conditionValues, parentContext);
}

export function createCollectionListQuerySpecification(
  collectionSlug: string,
  listCollection: CollectionType
): ElementQuerySpecification {
  collectionSlug = formatCollectionSlug(collectionSlug);

  function specifyCollectionListQueryPathPrefix(
    element: ExpressionElement,
    runtimeInfo: RuntimeInfo,
    parentContext: ParentContextInterface | null,
    exprContext: ExpressionEvaluationInterface
  ): QueryPath {
    const {paginationPageNumbers, publicationId, timezone, cmsLocaleId} =
      runtimeInfo;
    const paginationEnabled = isPaginatedDynamoWrapperElement(element);
    return getEdgesFieldPath(
      element,
      collectionSlug,
      listCollection,
      parentContext,
      paginationEnabled,
      paginationPageNumbers,
      publicationId,
      timezone,
      exprContext,
      cmsLocaleId
    );
  }

  function specifyCollectionListBoundaryElementQueryPaths(
    element: ExpressionElement,
    runtimeInfo: RuntimeInfo,
    parentContext: ParentContextInterface | null,
    exprContext: ExpressionEvaluationInterface
  ): QueryPaths {
    const paginationEnabled = isPaginatedDynamoWrapperElement(element);
    const queryPathPrefix = specifyCollectionListQueryPathPrefix(
      element,
      runtimeInfo,
      parentContext,
      exprContext
    );

    const queryPaths: QueryPath[] = [queryPathPrefix];
    if (paginationEnabled) {
      const countFieldPath = queryPathPrefix.slice(0, -1).concat(['count']);
      queryPaths.push(countFieldPath);
    }

    return queryPaths;
  }

  return {
    queryPathPrefix: specifyCollectionListQueryPathPrefix,
    boundaryElementQueryPaths: specifyCollectionListBoundaryElementQueryPaths,
    boundaryCreatorQueryPaths: specifyCollectionListBoundaryCreatorQueryPaths,
  };
}

export function createItemRefSetListQuerySpecification(
  field: Field
): ElementQuerySpecification {
  const queryPathPrefix = [
    `${formatFieldSlug(field.slug)}: resolveLocalItemRefSet(slug: "${field.slug}")`,
  ];

  return {
    queryPathPrefix,
    boundaryElementQueryPaths: [queryPathPrefix],
    boundaryCreatorQueryPaths: specifyItemRefSetListBoundaryCreatorQueryPaths,
    relative: true,
  };
}

function getDynamicContextQueryPaths(
  dynContextValues: string[],
  parentContext: ParentContextInterface
): QueryPath[] {
  if (dynContextValues.length === 0) {
    return [];
  }

  const dynContextQueryPaths: QueryPath[] = [];

  for (const value of dynContextValues) {
    if (
      isDynContextValueFilterValue(value) &&
      !isCorruptDynContextFilterValue(parentContext, value)
    ) {
      const fieldSlug = getDynContextValueFieldSlug(value);
      const bindingSource = parentContext.getBindingSource([fieldSlug]);
      if (bindingSource && bindingSource.queryPath.length > 0) {
        dynContextQueryPaths.push(bindingSource.queryPath);
      }
    }
  }

  if (dynContextQueryPaths.length === 0) {
    // there were only `DYN_CONTEXT` values, which only need context item id
    // and the id field is appended automatically if present
    return [[]];
  }

  return dynContextQueryPaths;
}

const notFoundButResume = Expressions.resume([]);
const notFoundAndStop = Expressions.stop([]);

const _getItemDynContextConditionValues = Expressions.everything<string[]>(
  concat
)(
  matchExpression({
    Element: (element) => {
      if (isDynamoItem(element)) {
        // DynamoItem belongs to the next binding context
        return notFoundAndStop;
      }

      if (isDynamoWrapper(element) || isDynamoList(element)) {
        // We don't support legacy conditional visibility on Collection List
        // and Collection List Wrapper elements.
        // Both support the visibility setting so we must resume search for
        // DYN_CONTEXT values from conditionals.
        // Also, the DynamoEmpty subtree can contain legacy conditional
        // visibility with DYN_CONTEXT conditions which is another reason to
        // resume the search.
        return notFoundButResume;
      }

      return Expressions.resume(getLegacyDynContextConditionValues(element));
    },
    Select: (select) => {
      const dynContextValue = getDynContextConditionValue(select);

      if (dynContextValue) {
        return Expressions.stop([dynContextValue]);
      }

      return notFoundButResume;
    },
    _: constant(notFoundButResume),
  })
);

const getItemDynContextConditionValues = (
  element: ExpressionElement
): string[] => {
  const dynamoItem = getDynamoItemElement(element);

  if (!dynamoItem) {
    return [];
  }

  return [
    // _getItemDynContextConditionValues stops on DynamoItem so we process it
    // to find DYN_CONTEXT values from legacy conditional visibility separately
    // if we passed EElement(dynamoItem) to _getItemDynContextConditionValues
    // the function call would stop immediately
    ...getLegacyDynContextConditionValues(dynamoItem),
    ..._getItemDynContextConditionValues(dynamoItem.data),
  ];
};

const isAnyDynContextFilterValue = (filterValue: string): boolean =>
  /^DYN_CONTEXT($|\..+$)/.test(filterValue);
const isDynContextValueFilterValue = (filterValue: string): boolean =>
  /^DYN_CONTEXT\..+$/.test(filterValue);
const isDynContextFilterValue = (filterValue: string): boolean =>
  filterValue === 'DYN_CONTEXT';
const getDynContextValueFieldSlug = (dynContextValue: string): string =>
  dynContextValue.replace('DYN_CONTEXT.', '');

const aliasFuncByFieldName = {
  items: itemsFieldAlias,
} as const;

const createAliasedFieldFormatter =
  (
    element: ExpressionElement,
    fieldName: 'items',
    instanceHash: string | null
  ) =>
  (args?: string | null): string => {
    const fieldAlias = aliasFuncByFieldName[fieldName](
      element.id,
      instanceHash
    );
    const fieldWithoutArgs = `${fieldAlias}: ${fieldName}`;
    return args ? `${fieldWithoutArgs}(${args})` : fieldWithoutArgs;
  };

const getItemsFieldPath = (
  element: ExpressionElement,
  collectionSlug: string,
  listCollection: CollectionType,
  parentContext: ParentContextInterface | null,
  paginationEnabled: boolean,
  paginationPageNumbers: PaginationPageNumbers,
  publicationId: string,
  timezone: string,
  exprContext: ExpressionEvaluationInterface,
  cmsLocaleId?: string | null
): QueryPath => {
  const {needsComponentData, value: itemsFieldArguments} =
    getListQueryArguments(
      element,
      listCollection,
      parentContext,
      paginationEnabled,
      paginationPageNumbers,
      publicationId,
      timezone,
      exprContext,
      cmsLocaleId
    );

  // we need to differentiate between paginated lists inside
  // components starting from page 2 onwards because page 1
  // content is always the same for every list
  const needsUniquePaginationAlias =
    paginationEnabled &&
    getPaginationPageNumber(paginationPageNumbers, [
      ...exprContext.instanceIdPath,
      element.id,
    ]) > 1;

  const formatItemsField = createAliasedFieldFormatter(
    element,
    'items',
    // differentiate with instance hash always when list query needs component
    // data or when pagination is enabled and page number is greater than 1
    needsUniquePaginationAlias || needsComponentData
      ? exprContext.instanceHash
      : null
  );

  const itemsFieldDef: FieldDef =
    typeof itemsFieldArguments === 'function'
      ? function getDataDependentItemsFieldDef(data) {
          return formatItemsField(itemsFieldArguments(data));
        }
      : formatItemsField(itemsFieldArguments);

  const cmsEnv = publicationId
    ? `publication: "${publicationId}"`
    : 'staging: true';

  return [
    `cms(${cmsEnv})`,
    `${collectionSlug}: collection(slug: "${listCollection.slug}")`,
    itemsFieldDef,
  ];
};

const getEdgesFieldPath = (
  element: ExpressionElement,
  collectionSlug: string,
  listCollection: CollectionType,
  parentContext: ParentContextInterface | null,
  paginationEnabled: boolean,
  paginationPageNumbers: PaginationPageNumbers,
  publicationId: string,
  timezone: string,
  exprContext: ExpressionEvaluationInterface,
  cmsLocaleId?: string | null
): QueryPath => {
  const itemsFieldPath = getItemsFieldPath(
    element,
    collectionSlug,
    listCollection,
    parentContext,
    paginationEnabled,
    paginationPageNumbers,
    publicationId,
    timezone,
    exprContext,
    cmsLocaleId
  );

  return [...itemsFieldPath, 'edges'];
};

const getListQueryArguments = (
  element: ExpressionElement,
  listCollection: CollectionType,
  parentContext: ParentContextInterface | null,
  paginationEnabled: boolean,
  paginationPageNumbers: PaginationPageNumbers,
  publicationId: string,
  timezone: string,
  exprContext: ExpressionEvaluationInterface,
  cmsLocaleId?: string | null
): ValueWithEvaluationManifest<FieldDef> => {
  let args: MaybeDataDependentValue<string | null>[];

  const localesArg = getLocalesArgument(element, exprContext, cmsLocaleId);

  const isCurated =
    getDynListQueryModeFromElement(element) ===
    COLLECTION_LIST_QUERY_MODES.CURATED;

  let needsComponentData = localesArg.needsComponentData;

  if (isCurated) {
    const curatedArg = getCuratedArgument(element, exprContext);

    args = [curatedArg.value, localesArg.value];
    needsComponentData = needsComponentData || curatedArg.needsComponentData;
  } else {
    const limitArg = getLimitArgument(
      element,
      paginationEnabled,
      paginationPageNumbers,
      exprContext
    );

    const offsetArg = getSkipArgument(
      element,
      paginationEnabled,
      paginationPageNumbers,
      exprContext
    );

    const sortArg = getSortArgument(
      element,
      listCollection,
      paginationEnabled,
      publicationId,
      exprContext
    );

    const filterArg = getFilterArgument(
      element,
      listCollection,
      parentContext,
      timezone,
      exprContext
    );

    args = [
      limitArg.value,
      offsetArg.value,
      sortArg.value,
      filterArg.value,
      localesArg.value,
    ];
    needsComponentData =
      needsComponentData ||
      limitArg.needsComponentData ||
      offsetArg.needsComponentData ||
      sortArg.needsComponentData ||
      filterArg.needsComponentData;
  }

  if (args.some((arg) => typeof arg === 'function')) {
    return {
      needsComponentData,
      value: function getDataDependentListQueryArguments(data) {
        return args
          .reduce<string[]>((expandedArgs, arg) => {
            const expandedArg = typeof arg === 'function' ? arg(data) : arg;

            if (expandedArg) {
              expandedArgs.push(expandedArg);
            }

            return expandedArgs;
          }, [])
          .join(', ');
      },
    };
  }

  return {needsComponentData, value: args.filter(Boolean).join(', ')};
};

function getLimitArgument(
  element: ExpressionElement,
  paginationEnabled: boolean,
  paginationPageNumbers: PaginationPageNumbers,
  exprContext: ExpressionEvaluationInterface
): MaybeDataDependentValueWithEvaluationManifest<string | null> {
  const limitResult = getQueryLimit(element, exprContext);
  const paginationLimitResult = getPaginationLimit(element, exprContext);

  const limitValue = limitResult.value;
  const paginationLimitValue = paginationLimitResult.value;

  const needsComponentData =
    limitResult.needsComponentData || paginationLimitResult.needsComponentData;

  if (
    typeof limitValue === 'function' ||
    typeof paginationLimitValue === 'function'
  ) {
    return {
      needsComponentData,
      value: function getDataDependentLimitArgument(data) {
        return deriveLimitArgumentFromValue(
          typeof limitValue === 'function' ? limitValue(data) : limitValue,
          typeof paginationLimitValue === 'function'
            ? paginationLimitValue(data)
            : paginationLimitValue,
          element,
          paginationEnabled,
          paginationPageNumbers,
          exprContext.instanceIdPath
        );
      },
    };
  }

  return {
    needsComponentData,
    value: deriveLimitArgumentFromValue(
      limitValue,
      paginationLimitValue,
      element,
      paginationEnabled,
      paginationPageNumbers,
      exprContext.instanceIdPath
    ),
  };
}

function deriveLimitArgumentFromValue(
  limitValue: number | null,
  paginationLimitValue: number,
  element: ExpressionElement,
  paginationEnabled: boolean,
  paginationPageNumbers: PaginationPageNumbers,
  instanceIdPath: string[]
): string | null {
  if (limitValue === null && !paginationEnabled) {
    return null;
  }

  const initialLimit = capDynamoWrapperLimit(paginationEnabled)(false)(
    Number(limitValue)
  );

  const limit = paginationEnabled
    ? calculatePaginationLimitQueryArg(
        getPaginationPageNumber(paginationPageNumbers, [
          ...instanceIdPath,
          element.id,
        ]),
        paginationLimitValue,
        initialLimit
      )
    : initialLimit;

  return `limit: ${Number(limit)}`;
}

const limitItemsPath: ExpressionPath = [
  {in: 'Record', at: 'dyn'},
  {in: 'Record', at: 'query'},
  {in: 'Record', at: 'limit'},
];

function getQueryLimit(
  element: ExpressionElement,
  exprContext: ExpressionEvaluationInterface
): MaybeDataDependentValueWithEvaluationManifest<number | null> {
  const limitExpr = Expressions.getIn(element.data, limitItemsPath);

  if (!limitExpr) {
    return {needsComponentData: false, value: null};
  }

  return evaluateAndParseExpression(limitExpr, exprContext, parseQueryLimit);
}

function parseQueryLimit(limitValue: unknown): number | null {
  if (typeof limitValue === 'number') {
    return Math.trunc(limitValue);
  }

  if (typeof limitValue === 'string') {
    const parsedLimitValue = Number(limitValue);
    if (!isNaN(parsedLimitValue)) {
      return Math.trunc(parsedLimitValue);
    }
  }

  return null;
}

const getSkipArgument = (
  element: ExpressionElement,
  paginationEnabled: boolean,
  paginationPageNumbers: PaginationPageNumbers,
  exprContext: ExpressionEvaluationInterface
): MaybeDataDependentValueWithEvaluationManifest<string | null> => {
  const skipResult = getQuerySkip(element, exprContext);
  const paginationLimitResult = getPaginationLimit(element, exprContext);

  const skipValue = skipResult.value;
  const paginationLimitValue = paginationLimitResult.value;

  const needsComponentData =
    skipResult.needsComponentData || paginationLimitResult.needsComponentData;

  if (
    typeof skipValue === 'function' ||
    typeof paginationLimitValue === 'function'
  ) {
    return {
      needsComponentData,
      value: function getDataDependentSkipArgument(data) {
        return deriveSkipArgumentFromValue(
          typeof skipValue === 'function' ? skipValue(data) : skipValue,
          typeof paginationLimitValue === 'function'
            ? paginationLimitValue(data)
            : paginationLimitValue,
          element,
          paginationEnabled,
          paginationPageNumbers,
          exprContext.instanceIdPath
        );
      },
    };
  }

  return {
    needsComponentData,
    value: deriveSkipArgumentFromValue(
      skipValue,
      paginationLimitValue,
      element,
      paginationEnabled,
      paginationPageNumbers,
      exprContext.instanceIdPath
    ),
  };
};

function deriveSkipArgumentFromValue(
  skipValue: number | null,
  paginationLimitValue: number,
  element: ExpressionElement,
  paginationEnabled: boolean,
  paginationPageNumbers: PaginationPageNumbers,
  instanceIdPath: string[]
) {
  if (skipValue === null && !paginationEnabled) {
    return null;
  }

  const initialSkip = Number(skipValue);

  const skip = paginationEnabled
    ? calculatePaginationSkipQueryArg(
        getPaginationPageNumber(paginationPageNumbers, [
          ...instanceIdPath,
          element.id,
        ]),
        paginationLimitValue,
        initialSkip
      )
    : initialSkip;

  return `skip: ${Number(skip)}`;
}

const dynQuerySkipPath: ExpressionPath = [
  {in: 'Record', at: 'dyn'},
  {in: 'Record', at: 'query'},
  {in: 'Record', at: 'offset'},
];

function getQuerySkip(
  element: ExpressionElement,
  exprContext: ExpressionEvaluationInterface
): MaybeDataDependentValueWithEvaluationManifest<number | null> {
  const skipExpr = Expressions.getIn(
    Expressions.getElementData(element),
    dynQuerySkipPath
  );

  if (!skipExpr) {
    return {needsComponentData: false, value: null};
  }

  return evaluateAndParseExpression(skipExpr, exprContext, parseQuerySkip);
}

function parseQuerySkip(skipValue: unknown): number | null {
  if (typeof skipValue === 'number') {
    return Math.trunc(skipValue);
  }

  if (typeof skipValue === 'string') {
    const parsedSkipValue = Number(skipValue);
    if (!isNaN(parsedSkipValue)) {
      return Math.trunc(parsedSkipValue);
    }
  }
  return null;
}

const paginationItemsPath: ExpressionPath = [
  {in: 'Record', at: 'pagination'},
  {in: 'Record', at: 'limit'},
];

function getPaginationLimit(
  element: ExpressionElement,
  exprContext: ExpressionEvaluationInterface
): MaybeDataDependentValueWithEvaluationManifest<number> {
  const paginationLimitExpr = Expressions.getIn(
    Expressions.getElementData(element),
    paginationItemsPath
  );

  if (!paginationLimitExpr) {
    return {needsComponentData: false, value: PAGINATION_MAX_LIMIT};
  }

  return evaluateAndParseExpression(
    paginationLimitExpr,
    exprContext,
    parsePaginationLimit
  );
}

function parsePaginationLimit(paginationLimit: unknown): number {
  if (typeof paginationLimit === 'number') {
    return clamp(1, PAGINATION_MAX_LIMIT, Math.trunc(paginationLimit));
  }
  return PAGINATION_MAX_LIMIT;
}

const getLocalesArgument = (
  element: ExpressionElement,
  exprContext: ExpressionEvaluationInterface,
  cmsLocaleId?: string | null
): MaybeDataDependentValueWithEvaluationManifest<string | null> => {
  if (cmsLocaleId == null || !/^[a-f\d]{24}$/i.test(cmsLocaleId)) {
    return {needsComponentData: false, value: null};
  }

  const usePrimaryLocaleExpr = Expressions.getIn(
    Expressions.getElementData(element),
    usePrimaryLocalePath
  );

  if (!usePrimaryLocaleExpr) {
    return {needsComponentData: false, value: `locales: ["${cmsLocaleId}"]`};
  }

  const result = evaluateAndParseExpression(
    usePrimaryLocaleExpr,
    exprContext,
    (value) => value === true
  );

  if (typeof result.value === 'function') {
    const getUsePrimaryLocaleValue = result.value;
    return {
      needsComponentData: result.needsComponentData,
      value: (data) => {
        const usePrimaryLocale = getUsePrimaryLocaleValue(data);

        if (usePrimaryLocale === true) {
          return null;
        }

        return `locales: ["${cmsLocaleId}"]`;
      },
    };
  }

  return {
    needsComponentData: result.needsComponentData,
    value: result.value === true ? null : `locales: ["${cmsLocaleId}"]`,
  };
};

/*
  getFilterArgument is called twice for paginated lists,
  so this procedure is memoized to prevent repeated parsing
  of filter fields and formatting them into the filter argument
 */
const getFilterArgument = memoize(
  (
    element: ExpressionElement,
    listCollection: CollectionType,
    parentContext: ParentContextInterface | null,
    timezone: string,
    exprContext: ExpressionEvaluationInterface
  ): MaybeDataDependentValueWithEvaluationManifest<string> => {
    const fieldsResult = getFilterFieldsArray(element, exprContext);
    const filterForResult = getFilterFor(element, exprContext);

    const fields = fieldsResult.value;
    const filterForValue = filterForResult.value;

    const needsComponentData =
      fieldsResult.needsComponentData || filterForResult.needsComponentData;

    if (typeof fields === 'function' || typeof filterForValue === 'function') {
      return {
        needsComponentData,
        value: function getDataDependentFilterArgument(data: RawData): string {
          const filterForKey =
            FILTER_FOR_KEY_MAP[
              typeof filterForValue === 'function'
                ? filterForValue(data)
                : filterForValue
            ];

          const filterFieldsFragment = formatFilterFields(
            filterForKey,
            typeof fields === 'function' ? fields(data) : fields,
            listCollection,
            parentContext,
            timezone
          );

          const filterArgument = formatFilterArgument(
            filterFieldsFragment,
            filterForKey
          );

          return typeof filterArgument === 'function'
            ? filterArgument(data)
            : filterArgument;
        },
      };
    }

    const filterForKey = FILTER_FOR_KEY_MAP[filterForValue];
    const filterFieldsFragment = formatFilterFields(
      filterForKey,
      fields,
      listCollection,
      parentContext,
      timezone
    );

    return {
      needsComponentData,
      value: formatFilterArgument(filterFieldsFragment, filterForKey),
    };
  }
);

function formatFilterArgument(
  filterFieldsFragment: FieldDef,
  filterForKey: FilterAllKey | FilterAnyKey
): FieldDef {
  if (!filterFieldsFragment) {
    return '';
  }

  if (typeof filterFieldsFragment === 'function') {
    return function getDataDependentFilterArgument(data) {
      return `filter: {${filterForKey}: [${filterFieldsFragment(data)}]}`;
    };
  }

  return `filter: {${filterForKey}: [${filterFieldsFragment}]}`;
}

const combineFilterFields = (
  filterForKey: FilterAllKey | FilterAnyKey,
  formattedFields: string[]
) => {
  if (formattedFields.length === 0) {
    return '';
  }

  return formattedFields
    .map((fieldString) =>
      filterForKey === 'matchAny'
        ? `{matchAll: [{${fieldString}}]}`
        : `{${fieldString}}`
    )
    .join(', ');
};

const formatFilterFields = (
  filterForKey: FilterAllKey | FilterAnyKey,
  fields: ConditionsT,
  listCollection: CollectionType,
  parentContext: ParentContextInterface | null,
  timezone: string
): FieldDef => {
  const formatFilterField = createFilterFieldFormatter(
    listCollection,
    parentContext,
    timezone
  );

  // format individual filter fields to ensure valid operators and values
  const formattedFields: Array<string | FieldDefOrNull> = [];
  const stringFields: string[] = [];
  const filterFields = [...fields];

  for (const field of filterFields) {
    const formatted = formatFilterField(field);
    if (formatted) {
      formattedFields.push(formatted);

      if (typeof formatted === 'string') {
        stringFields.push(formatted);
      }
    }
  }

  if (stringFields.length === formattedFields.length) {
    return combineFilterFields(filterForKey, stringFields);
  }

  // there is at least one `DYN_CONTEXT` filter, so we need context item data
  return function getDataDependentFilterFields(data) {
    return combineFilterFields(
      filterForKey,
      formattedFields.reduce<string[]>((memo, field) => {
        const expandedField = typeof field === 'function' ? field(data) : field;
        if (expandedField) {
          memo.push(expandedField);
        }
        return memo;
      }, [])
    );
  };
};

const isCorruptDynContextFilterValue = (
  parentContext: ParentContextInterface | null,
  value: any
): boolean => {
  if (Array.isArray(value)) {
    return value.some((filterValue) => {
      return isCorruptDynContextFilterValue(parentContext, filterValue);
    });
  }

  if (!isAnyDynContextFilterValue(value)) {
    return false;
  }

  if (!parentContext) {
    return true;
  }

  if (isDynContextFilterValue(value)) {
    return false;
  }

  const dynContextValueFieldSlug = getDynContextValueFieldSlug(value);

  const bindingSource = parentContext.getBindingSource([
    dynContextValueFieldSlug,
  ]);

  // DYN_CONTEXT.value field is missing or not a reference field
  return (
    !bindingSource ||
    (bindingSource.type !== 'ItemRef' && bindingSource.type !== 'ItemRefSet')
  );
};

const createFilterFieldFormatter =
  (
    listCollection: CollectionType,
    parentContext: ParentContextInterface | null,
    timezone: string
  ) =>
  (field: ConditionT): MaybeDataDependentValue<string | null> => {
    const value = field.value ?? '';
    const isDynContextFilter =
      value && Array.isArray(value)
        ? value.some(isAnyDynContextFilterValue)
        : isAnyDynContextFilterValue(value);

    if (!isDynContextFilter) {
      return formatFilterField(field, listCollection, timezone);
    }

    if (!parentContext) {
      return null;
    }

    if (isCorruptDynContextFilterValue(parentContext, value)) {
      // If the contextCollection does not have a field that matches
      // the slug in DYN_CONTEXT.value filter, we have a corrupt filter
      // that we want to ignore entirely by returning null.
      return null;
    }

    return function getDataDependentFilterField(data) {
      const updatedValue = replaceDynContextFilterValue(
        field.value,
        data,
        parentContext
      );

      const updatedField = {...field, value: updatedValue} as const;

      return formatFilterField(updatedField, listCollection, timezone);
    };
  };

/*
  Formats a filter condition into a GraphQL fragment

  If the filter condition is corrupted, i.e. there is no field path
  or operator, or filter value could not be parsed, then this function
  returns null.
 */
const formatFilterField = (
  field: ConditionT,
  listCollection: CollectionType,
  timezone: string
): string | null => {
  const {fieldPath: fieldSlug, operatorName: operator} = field;

  if (!fieldSlug || !operator) {
    return null;
  }

  const fieldValue = field.value ?? '';

  const fieldType = getFieldType(listCollection, fieldSlug);

  if (!fieldType) {
    return null;
  }

  const parsedFilterTerm = parseFilterTerm(
    operator,
    fieldValue,
    fieldType,
    timezone,
    listCollection,
    fieldSlug
  );

  if (!parsedFilterTerm) {
    return null;
  }

  const {operatorName, filter} = parsedFilterTerm;

  return formatFilter(operatorName, filter);
};

function replaceDynContextFilterValue(
  value: string | string[],
  data: any,
  parentContext: ParentContextInterface | null
): string | string[] {
  if (typeof value !== 'string') {
    return value.map((val) =>
      replaceSingleDynContextFilterValue(val, data, parentContext)
    );
  }

  return replaceSingleDynContextFilterValue(value, data, parentContext);
}

function replaceSingleDynContextFilterValue(
  value: string,
  data: any,
  parentContext: ParentContextInterface | null
): string {
  if (isDynContextFilterValue(value)) {
    return get(data, 'id', NON_EXISTING_ITEM_ID);
  }

  const slug = getDynContextValueFieldSlug(value);
  const fieldSlug = formatFieldSlug(slug);

  const bindingSource = parentContext
    ? parentContext.getBindingSource([slug])
    : null;
  const fieldType = bindingSource ? bindingSource.type : 'PlainText';

  switch (fieldType) {
    case 'Option':
    case 'ItemRef': {
      // The query expects to have a valid id in the filter so adding 24 zeros
      // as a mock id if item doesn't have the needed Option or ItemRef field.
      return get(data, [fieldSlug, 'id'], NON_EXISTING_ITEM_ID);
    }

    case 'ItemRefSet': {
      const itemRefSetValue = get(data, fieldSlug);
      // ItemRefSet fields are nullable, so ensure map is called on an array
      return (itemRefSetValue || []).map(
        ({id}: any) => id || NON_EXISTING_ITEM_ID
      );
    }

    case 'Video':
    case 'ImageRef':
    case 'FileRef': {
      return get(data, [fieldSlug, 'url'], '');
    }

    case 'Number': {
      const number = get(data, fieldSlug);
      // @ts-expect-error - TS2322 - Type 'number' is not assignable to type 'string'.
      return isNil(number) ? NON_EXISTING_NUMBER : number;
    }

    default: {
      return get(data, fieldSlug, '');
    }
  }
}

function isTypeSupportingBooleanAny(type: string) {
  return (
    type === 'RichText' ||
    type === 'ImageRef' ||
    type === 'Video' ||
    type === 'Link' ||
    type === 'FileRef' ||
    type === 'Bool'
  );
}

function isTypeSupportingTextEmpty(type: string) {
  return (
    type === 'PlainText' ||
    type === 'Option' ||
    type === 'Email' ||
    type === 'Phone'
  );
}

const GRAPHQL_OPERATOR_PREFIX_BY_TYPE: Record<string, string> = Object.freeze({
  DateTime: 'Date',
});

const GRAPHQL_OPERATOR_SUFFIX_BY_OPERATOR_NAME: Record<string, string> =
  Object.freeze({
    exists: 'Exists',
    eq: 'Eq',
    ne: 'Ne',
    gt: 'Gt',
    gte: 'Gte',
    lt: 'Lt',
    lte: 'Lte',
    in: 'In',
    nin: 'Nin',
  });

/*
  Parses filter operator and value

  Converts operator name to be compatible with MongoDB and
  casts value based on the Dynamo field type.

  Handles edge cases of some operator - field type combinations
  by eliminating conditions that have corrupt values and doing
  additional casting or conversion of operator and/or value.
 */
const parseFilterTerm = (
  inputOperatorName: string,
  value: unknown,
  type: string,
  timezone: string,
  listCollection: CollectionType,
  fieldSlug: string
): {
  operatorName: string;
  filter: {field: {slug: string}; value: unknown};
} | null => {
  const mongoOperatorName = convertToMongoOperator(inputOperatorName);

  let operatorPrefix: string;

  if (fieldSlug === '_id') {
    operatorPrefix = 'PrimaryKey';
  } else if (GRAPHQL_OPERATOR_PREFIX_BY_TYPE[type]) {
    operatorPrefix = GRAPHQL_OPERATOR_PREFIX_BY_TYPE[type];
  } else {
    operatorPrefix = type;
  }

  const operatorSuffix =
    GRAPHQL_OPERATOR_SUFFIX_BY_OPERATOR_NAME[mongoOperatorName] ?? '';

  if (!operatorSuffix) {
    return null;
  }

  const operatorName = `${operatorPrefix}${operatorSuffix}`;

  if (mongoOperatorName === 'exists') {
    // drop filter field with exists any as it is the same as no filter
    if (value === 'any') {
      return null;
    }

    return {
      operatorName,
      filter: {
        field: {slug: fieldSlug},
        value: value === 'yes' || value === 'true',
      },
    };
  }

  if (
    mongoOperatorName === 'eq' &&
    value === 'any' &&
    isTypeSupportingBooleanAny(type)
  ) {
    // drop filter field with equals any as it is the same as no filter
    return null;
  }

  const castedValue = castItemValue({
    value,
    operator: mongoOperatorName,
    type: type === 'DateTime' ? 'Date' : type,
    timezone,
  });

  if (type === 'Bool' && castedValue === false) {
    // Here we need to also consider null values as false so flipping the key and value so that i.e.
    // "equals False" becomes "does not equal True" and "does not equal False" becomes "equals True".
    return {
      operatorName: `${operatorPrefix}${mongoOperatorName === 'eq' ? 'Ne' : 'Eq'}`,
      filter: {
        field: {slug: fieldSlug},
        value: !castedValue,
      },
    };
  }

  if (
    mongoOperatorName === 'eq' &&
    castedValue === '' &&
    isTypeSupportingTextEmpty(type)
  ) {
    // drop filter field with empty value as it is the same as no filter
    return null;
  }

  if (type === 'Number' && !isFinite(castedValue)) {
    // drop filter field with corrupt number value
    return null;
  }

  if (type === 'Date' || type === 'DateTime') {
    if (!castedValue) {
      // drop filter field with corrupt date value
      return null;
    }

    const dateOrDateTimeValue =
      type === 'Date' ? getUtcDate(castedValue, timezone) : castedValue;

    return {
      operatorName,
      filter: {
        field: {slug: fieldSlug},
        value: dateOrDateTimeValue.toISOString(),
      },
    };
  }

  if (type === 'Option') {
    const field = getFieldBySlug(listCollection, fieldSlug);
    if (!field) {
      return {
        operatorName,
        filter: {
          field: {slug: fieldSlug},
          value: castedValue,
        },
      };
    }

    const options = field.validations.get('options');
    if (!options || typeof options !== 'object' || !isImmutableList(options)) {
      return {
        operatorName,
        filter: {
          field: {slug: fieldSlug},
          value: castedValue,
        },
      };
    }

    const matchingOption = options.find((option: unknown) => {
      if (option && typeof option === 'object' && isImmutableMap(option)) {
        const name = option.get('name');
        return name === castedValue;
      }
      return false;
    });

    if (matchingOption && isImmutableMap(matchingOption)) {
      return {
        operatorName,
        filter: {
          field: {slug: fieldSlug},
          value: matchingOption.get('id'),
        },
      };
    }
  }

  return {
    operatorName,
    filter: {
      field: {slug: fieldSlug},
      value: castedValue,
    },
  };
};

function isImmutableList(value: unknown): value is List<unknown> {
  return List.isList(value);
}

function isImmutableMap(value: unknown): value is Map<string, any> {
  return Map.isMap(value);
}

// Converts a operator name incompatible with MongoDB to its MongoDB equivalent
const convertToMongoOperator = (operatorName: string): string => {
  switch (operatorName) {
    case 'idin':
      return 'in';
    case 'idnin':
      return 'nin';
    default:
      return operatorName;
  }
};

const getUtcDate = (dateTime: any, timezone: string) => {
  // Dates without time are stored as UTC time. When a dynamic list filter is applied,
  // the stored date is compared with the value in the query filter. Even if today
  // is 2018/04/19 in UTC it can be already 2018/04/20 in Tonga. If the site's timezone
  // is Tonga and we filter, for example, items that are created 2018/04/20 Tonga time,
  // we need to first parse the "today" filter value as Tonga time and then force that
  // value as it was in UTC. Otherwise we would be comparing item creation time, that is
  // stored in UTC time to "today" in Tonga, which could give us items from the previous
  // day in some cases.
  return moment
    .utc(moment.tz(dateTime, timezone).format('YYYY-MM-DD'))
    .toDate();
};

const formatFilter = (
  operatorName: string,
  filter: {field: {slug: string}; value: unknown}
): string =>
  `${operatorName}: {field: {slug: ${JSON.stringify(filter.field.slug)}}, value: ${JSON.stringify(filter.value)}}`;

const getSortArgument = (
  element: ExpressionElement,
  listCollection: CollectionType,
  paginationEnabled: boolean,
  publicationId: string,
  exprContext: ExpressionEvaluationInterface
): ValueWithEvaluationManifest<string | null> => {
  const sortExpr = Expressions.getIn(
    Expressions.getElementData(element),
    dynQuerySortPath
  );

  if (!sortExpr) {
    return {needsComponentData: false, value: null};
  }

  const {needsComponentData, value} = evaluateAndParseExpression(
    sortExpr,
    exprContext,
    parseSortArray
  );

  if (value === null || value.length === 0 || typeof value === 'function') {
    return {needsComponentData: false, value: null};
  }

  const isRandomized = value.some(isRandomSortingKey);

  if (isRandomized) {
    return {
      needsComponentData,
      value: getShuffleArguments(element, paginationEnabled, publicationId),
    };
  }

  const sort = value
    .map(sortingKeyToObject)
    .filter(isValidSortingField(listCollection))
    .map(formatSortingKey);

  return {needsComponentData, value: `sort: [${sort.join(', ')}]`};
};

function parseSortArray(sorts: unknown): SortData[] {
  if (Array.isArray(sorts)) {
    return sorts;
  }
  return [];
}

const getCuratedArgument = (
  element: ExpressionElement,
  exprContext: ExpressionEvaluationInterface
): ValueWithEvaluationManifest<string> => {
  const curatedExpr = Expressions.getIn(
    Expressions.getElementData(element),
    dynQueryCuratedPath
  );

  if (!curatedExpr) {
    return {needsComponentData: false, value: ''};
  }

  const result = evaluateAndParseExpression(
    curatedExpr,
    exprContext,
    parseCuratedItemIds
  );

  const {needsComponentData, value: curatedIds} = result;

  if (!curatedIds || typeof curatedIds === 'function') {
    return {needsComponentData: false, value: ''};
  }

  const curatedIdsArrayString =
    curatedIds.length === 0 ? '[]' : `["${curatedIds.join('","')}"]`;

  return {
    needsComponentData,
    value: `filter: {matchAll: [{PrimaryKeyIn: {value: ${curatedIdsArrayString}}}]}`,
  };
};

function parseCuratedItemIds(ids: unknown): string[] {
  if (
    ids &&
    typeof ids === 'object' &&
    'itemIds' in ids &&
    Array.isArray(ids.itemIds)
  ) {
    return ids.itemIds.filter((itemId) => typeof itemId === 'string');
  }
  return [];
}

const getShuffleArguments = (
  element: ExpressionElement,
  paginationEnabled: boolean,
  publicationId: string
): string => {
  const elementId = element.id;
  // We use `shuffleKey` to distinguish similar list queries with random
  // sorting, so that Apollo client caches the lists separately.
  const baseShuffleArgs = `enabled: true, key: "${elementId}"`;
  return `shuffle: {${
    paginationEnabled
      ? /*
      For a paginated collection list, random sorting needs to be less random.
      For example, if the first page of a paginated, randomly sorted list has
      a set of items, we don't want any of those items to re-appear on the
      following pagination pages, as each pagination page is its own hosted
      page rendered server-side. Using a `shuffleSeed` the GraphQL API can make
      sure the random sorting does not change until the site is re-published.
    */
        `${baseShuffleArgs}, seed: "${publicationId}${elementId}"`
      : baseShuffleArgs
  }}`;
};

const isRandomSortingKey = (sort: SortData): boolean => {
  return sort.field === '_random';
};

type SortingObject = {
  slug: string;
  descending: boolean;
};

const sortingKeyToObject = (sort: SortData): SortingObject => {
  const descending = sort.direction === 'desc';
  const slug = sort.field;

  return {slug, descending};
};

const isValidSortingField =
  (collection: CollectionType) =>
  ({slug}: SortingObject) =>
    Boolean(getFieldBySlug(collection, slug));

const formatSortingKey = ({slug, descending}: SortingObject) =>
  `{field: {slug: "${slug}"}, dir: ${descending ? 'DESC' : 'ASC'}}`;

const getFieldType = (
  collection: CollectionType,
  slug: string
): string | null => {
  const field = getFieldBySlug(collection, slug);
  const fieldType = field ? field.get('type', null) : null;

  if (field && fieldType === 'Date') {
    /*
      Dynamo Field types do not distinguish between Date and Datetime,
      but some functionalities depend on this distinction (*cough* Collection
      List filter value casting *cough*). That disctinction is made here.
     */
    return isDateTimeField(field) || isNonConfigurableDateField(field)
      ? 'DateTime'
      : 'Date';
  }

  if (NON_CUSTOM_FIELDS_TYPES.has(slug) || slug === '_id') {
    return 'PlainText';
  }

  return fieldType ?? null;
};

const isDateTimeField = (field: RecordOf<FieldProps>): boolean =>
  field.getIn(['validations', 'format']) === 'date-time';

const isNonConfigurableDateField = (field: RecordOf<FieldProps>): boolean => {
  const slug = field.get('slug');
  return (
    slug === 'created-on' || slug === 'updated-on' || slug === 'published-on'
  );
};

function getFilterFor(
  element: ExpressionElement,
  exprContext: ExpressionEvaluationInterface
): MaybeDataDependentValueWithEvaluationManifest<
  keyof typeof QUERY_FILTER_FOR_STATES
> {
  const filterExpr = Expressions.getIn(
    Expressions.getElementData(element),
    dynQueryFilterPath
  );
  if (!filterExpr) {
    return {needsComponentData: false, value: QUERY_FILTER_FOR_STATES.ALL};
  }
  return evaluateAndParseExpression(filterExpr, exprContext, parseFilterFor);
}

function parseFilterFor(
  filterFields: unknown
): keyof typeof QUERY_FILTER_FOR_STATES {
  if (
    filterFields &&
    typeof filterFields === 'object' &&
    'filterFor' in filterFields &&
    (filterFields.filterFor === QUERY_FILTER_FOR_STATES.ALL ||
      filterFields.filterFor === QUERY_FILTER_FOR_STATES.ANY)
  ) {
    return filterFields.filterFor;
  }
  return QUERY_FILTER_FOR_STATES.ALL;
}

function getFilterFieldsArray(
  element: ExpressionElement,
  exprContext: ExpressionEvaluationInterface
): MaybeDataDependentValueWithEvaluationManifest<ConditionsT> {
  const filterExpr = Expressions.getIn(
    Expressions.getElementData(element),
    dynQueryFilterPath
  );

  if (!filterExpr) {
    return {needsComponentData: false, value: []};
  }

  return evaluateAndParseExpression(
    filterExpr,
    exprContext,
    parseFilterFieldsArray
  );
}

function parseFilterFieldsArray(filterFields: unknown): ConditionsT {
  if (
    filterFields &&
    typeof filterFields === 'object' &&
    'fields' in filterFields &&
    Array.isArray(filterFields.fields)
  ) {
    return filterFields.fields;
  }
  return [];
}

function evaluateAndParseExpression<ParsedValueT>(
  expr: Expression,
  exprContext: ExpressionEvaluationInterface,
  parseResultValue: (value: unknown) => ParsedValueT
): MaybeDataDependentValueWithEvaluationManifest<ParsedValueT> {
  const result = exprContext.evaluateExpr(expr);

  if (result.type === 'dynamic') {
    return {
      needsComponentData: result.needsComponentData,
      value: function getDataDependentValue() {
        return parseResultValue(result.valueCallback());
      },
    };
  }

  return {
    needsComponentData: result.needsComponentData,
    value: parseResultValue(result.value),
  };
}

function getDynContextFilterValues(
  element: ExpressionElement,
  exprContext: ExpressionEvaluationInterface
): string[] {
  const filterExpr = Expressions.getIn(
    Expressions.getElementData(element),
    dynQueryFilterPath
  );
  const filterValues: string[] = [];

  if (filterExpr) {
    exprContext.inspectExpr(
      filterExpr,
      function collectDynContextFilterValues(expr) {
        filterValues.push(...getDynContextFilterValuesFromFilterExpr(expr));
      }
    );
  }

  return filterValues;
}
